// =====================================================================
// Board State model + serialization
// =====================================================================
// 仕様準拠: closing_river_v9_spec.md §1, §4
//
// 設計:
//   - GameState は immutable (各操作で新インスタンスを返す)
//   - Map は ReadonlyMap として公開、内部書換は build helper のみ
//   - 連結判定 / 隣接抽出のヘルパーをここに集約
// =====================================================================
/** 空の盤面 state (生成器が一から積み上げるための基底) */
export function emptyState(seed, turnLimit) {
    return {
        seed,
        nodes: new Map(),
        activeEdges: new Map(),
        historyEdges: new Map(),
        movesUsed: 0,
        turnLimit,
        nextNodeId: 1,
        nextEdgeId: 1,
        nextHistoryEdgeId: 1,
        currentStep: 0,
    };
}
/** GameState の浅いコピー + 部分上書き */
export function withChanges(state, patch) {
    return { ...state, ...patch };
}
/** ノード追加 (新 state を返す) */
export function addNode(state, value) {
    if (value < 1 || value > 99 || !Number.isInteger(value)) {
        throw new Error(`addNode: value must be integer in 1..99, got ${value}`);
    }
    const id = state.nextNodeId;
    const nodes = new Map(state.nodes);
    nodes.set(id, { id, value });
    return {
        state: withChanges(state, { nodes, nextNodeId: id + 1 }),
        id,
    };
}
/** ノード値を更新 (新 state を返す) */
export function setNodeValue(state, id, value) {
    if (value < 1 || value > 99 || !Number.isInteger(value)) {
        throw new Error(`setNodeValue: value must be integer in 1..99, got ${value}`);
    }
    const nodes = new Map(state.nodes);
    const existing = nodes.get(id);
    if (!existing)
        throw new Error(`setNodeValue: node ${id} not found`);
    nodes.set(id, { id, value });
    return withChanges(state, { nodes });
}
/** ノード削除 */
export function removeNode(state, id) {
    const nodes = new Map(state.nodes);
    if (!nodes.delete(id))
        throw new Error(`removeNode: node ${id} not found`);
    return withChanges(state, { nodes });
}
/** active 辺追加 (自己ループは禁止) */
export function addActiveEdge(state, src, dst) {
    if (src === dst) {
        throw new Error(`addActiveEdge: self-loop not allowed (src=dst=${src})`);
    }
    if (!state.nodes.has(src))
        throw new Error(`addActiveEdge: src ${src} not found`);
    if (!state.nodes.has(dst))
        throw new Error(`addActiveEdge: dst ${dst} not found`);
    const id = state.nextEdgeId;
    const activeEdges = new Map(state.activeEdges);
    activeEdges.set(id, { id, src, dst });
    return {
        state: withChanges(state, { activeEdges, nextEdgeId: id + 1 }),
        id,
    };
}
/** active 辺を history へ移動 (consumed | self_loop) */
export function moveEdgeToHistory(state, edgeId, reason) {
    const activeEdges = new Map(state.activeEdges);
    const edge = activeEdges.get(edgeId);
    if (!edge)
        throw new Error(`moveEdgeToHistory: edge ${edgeId} not found`);
    activeEdges.delete(edgeId);
    const historyEdges = new Map(state.historyEdges);
    const historyId = state.nextHistoryEdgeId;
    const histEntry = {
        id: historyId,
        sourceActiveEdgeId: edgeId,
        deletedAtStep: state.currentStep,
        deletedReason: reason,
        endpointNodeIdsAtDeletion: [edge.src, edge.dst],
        endpointPositionsAtDeletion: null,
        geometrySnapshot: null,
    };
    historyEdges.set(historyId, histEntry);
    return withChanges(state, {
        activeEdges,
        historyEdges,
        nextHistoryEdgeId: historyId + 1,
    });
}
/** active 辺の端点を rewire (合流処理 §3 で使う) */
export function rewireEdge(state, edgeId, newSrc, newDst) {
    const activeEdges = new Map(state.activeEdges);
    const edge = activeEdges.get(edgeId);
    if (!edge)
        throw new Error(`rewireEdge: edge ${edgeId} not found`);
    activeEdges.set(edgeId, { id: edgeId, src: newSrc, dst: newDst });
    return withChanges(state, { activeEdges });
}
/** あるノードに接続する active 辺の id 一覧 (順序: edge_id 昇順、決定論) */
export function incidentEdges(state, nodeId) {
    const ids = [];
    for (const [id, e] of state.activeEdges) {
        if (e.src === nodeId || e.dst === nodeId)
            ids.push(id);
    }
    return ids.sort((a, b) => a - b);
}
/** active グラフの隣接リスト構築 */
export function buildAdjacency(state) {
    const adj = new Map();
    for (const id of state.nodes.keys())
        adj.set(id, []);
    for (const e of state.activeEdges.values()) {
        adj.get(e.src).push(e.dst);
        adj.get(e.dst).push(e.src);
    }
    return adj;
}
/** active グラフの連結成分数 (§4 非連結敗北判定) */
export function countComponents(state) {
    const adj = buildAdjacency(state);
    const visited = new Set();
    let components = 0;
    for (const start of state.nodes.keys()) {
        if (visited.has(start))
            continue;
        components++;
        const stack = [start];
        while (stack.length > 0) {
            const u = stack.pop();
            if (visited.has(u))
                continue;
            visited.add(u);
            for (const v of adj.get(u)) {
                if (!visited.has(v))
                    stack.push(v);
            }
        }
    }
    return components;
}
/** §6 接続候補プレビュー (active 辺ごとに操作後値を計算) */
export function buildEdgePreviews(state) {
    const previews = [];
    // edge_id 昇順で決定論的に
    const ids = Array.from(state.activeEdges.keys()).sort((a, b) => a - b);
    for (const id of ids) {
        const e = state.activeEdges.get(id);
        const u = state.nodes.get(e.src);
        const v = state.nodes.get(e.dst);
        let kind;
        let resultPreview;
        if (u.value === v.value) {
            kind = 'merge';
            resultPreview = u.value;
        }
        else {
            kind = 'subtract';
            // 大きい方が小さい方を引いた後の値
            resultPreview = Math.abs(u.value - v.value);
        }
        previews.push({
            edgeId: id,
            srcId: e.src,
            dstId: e.dst,
            srcValue: u.value,
            dstValue: v.value,
            operationKind: kind,
            resultPreview,
            isMergeCandidate: kind === 'merge',
        });
    }
    return previews;
}
export function serialize(state) {
    return {
        seed: state.seed,
        nodes: Array.from(state.nodes.values()),
        activeEdges: Array.from(state.activeEdges.values()),
        historyEdges: Array.from(state.historyEdges.values()),
        movesUsed: state.movesUsed,
        turnLimit: state.turnLimit,
        nextNodeId: state.nextNodeId,
        nextEdgeId: state.nextEdgeId,
        nextHistoryEdgeId: state.nextHistoryEdgeId,
        currentStep: state.currentStep,
    };
}
export function deserialize(s) {
    return {
        seed: s.seed,
        nodes: new Map(s.nodes.map((n) => [n.id, n])),
        activeEdges: new Map(s.activeEdges.map((e) => [e.id, e])),
        historyEdges: new Map(s.historyEdges.map((h) => [h.id, h])),
        movesUsed: s.movesUsed,
        turnLimit: s.turnLimit,
        nextNodeId: s.nextNodeId,
        nextEdgeId: s.nextEdgeId,
        nextHistoryEdgeId: s.nextHistoryEdgeId,
        currentStep: s.currentStep,
    };
}
/** ノード値の gcd を計算 (§5 終端値検証用) */
export function nodeValueGcd(state) {
    let g = 0;
    for (const n of state.nodes.values())
        g = gcd2(g, n.value);
    return g;
}
function gcd2(a, b) {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
        [a, b] = [b, a % b];
    }
    return a;
}
//# sourceMappingURL=boardState.js.map